import { AxiosResponse } from "axios";
import React, { Fragment } from "react";
import * as Sentry from "@sentry/browser";
import { RouteComponentProps } from "react-router";

import { AppstoreOutlined, EditOutlined, PlusOutlined, MessageOutlined } from "@ant-design/icons";
import { Button, Col, Row, Table, message } from "antd";
import { NavLink } from "react-router-dom";
import Card from "../components/Card";
import TemplateTypeTag from "../components/TemplateTypeTag";
import EntitledSection from "../components/EntitledSection";
import entitlements from "../config/entitlements";
import { Template, TemplateCategory } from "../helpers/shared-interfaces";
import { request , showTotalPagination } from "../helpers/utilities";
import { GET_TEMPLATES, GET_TEMPLATE_CATEGORIES } from "../gql/templates";
import { PageToolbar } from "../components/PageToolbar";
import { generateDisplayErrorMessage } from "../helpers/utilities";


interface State {
  isLoading: boolean;
  templates: Template[];
  templateCategories: TemplateCategory[];
  selectedTypes: string[];
  selectedCategoryIds: string[];
  searchTerm: string;
  isFiltering: boolean;
  page: number;
  pageSize: number;
  templatesCount: number;
}

export const columns = [
  {
    title: "Template Name",
    dataIndex: "name",
    key: "Template Name",
    width: "40%",
    render(text: string, record: Template): JSX.Element {
      return (
        <NavLink to={`/communication-templates/${record.id}`}>{text}</NavLink>
      );
    },
  },
  {
    title: "Category",
    dataIndex: "templateCategory",
    key: "category",
    render(templateCategory?: TemplateCategory): JSX.Element {
      return <span>{templateCategory?.name || "Uncategorized"}</span>;
    },
  },
  {
    title: "Type",
    dataIndex: "templateType",
    key: "templateType",
    render(text: string): JSX.Element {
      return <TemplateTypeTag text={text} />;
    },
  },
  {
    title: "Action",
    dataIndex: "action",
    key: "action",
    align: "center" as const,
    render(text: string, record: Template): JSX.Element {
      return (
        <span>
          <NavLink
            to={`/communication-templates/${record.id}/edit?name=${record.name}`}
          >
            <EditOutlined />
          </NavLink>
        </span>
      );
    },
  },
];

export function TemplatesTableFactory(
  data: Template[],
  page = 1,
  pageSize = 10,
  total = data.length,
  onPageChange?: (page: number, pageSize?: number) => void,
): JSX.Element {
  return (
    <Table
      scroll={{ x: 700 }}
      style={{ width: "100%" }}
      columns={columns}
      dataSource={data.map(
        (template: Template): Template => ({
          ...template,
          key: template.id,
        }),
      )}
      pagination={{
        current: page,
        pageSize,
        total,
        onChange: onPageChange,
        showTotal: showTotalPagination,
      }}
    />
  );
}

export default class EmailTemplates extends React.Component<
  RouteComponentProps,
  State
> {
  _isMounted = false;

  constructor(props: Readonly<RouteComponentProps>) {
    super(props);
    this.state = {
      isLoading: true,
      templates: [],
      templateCategories: [],
      selectedTypes: [],
      selectedCategoryIds: [],
      searchTerm: "",
      isFiltering: false,
      page: 1,
      pageSize: 10,
      templatesCount: 0,
    };
  }

  componentDidMount(): void {
    this._isMounted = true;
    this.setState({ isLoading: true }, () => {
      this.getEmailTemplates();
      this.getTemplateCategories();
    });
  }

  getEmailTemplates = async (): Promise<void> => {
    const { page, pageSize, searchTerm, selectedTypes, selectedCategoryIds } =
      this.state;
    try {
      const {
        data: { data },
      }: AxiosResponse<{
        data: {
          templatesFilter: {
            count: number;
            results: Template[];
          };
        };
      }> = await request({
        query: GET_TEMPLATES,
        variables: {
          page,
          q: searchTerm || undefined,
          templateType: selectedTypes[0] || undefined,
          templateCategoryId: selectedCategoryIds[0] || undefined,
          numberPerPage: pageSize,
        },
      });

      this.setState({
        templates: data.templatesFilter.results,
        templatesCount: data.templatesFilter.count,
        isLoading: false,
      });
    } catch (error) {
      message.error(`Failed to load templates - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
      this.setState({ isLoading: false });
    }
  };

  getTemplateCategories = async (): Promise<void> => {
    try {
      const {
        data: { data },
      }: AxiosResponse<{
        data: {
          templateCategories: {
            results: TemplateCategory[];
          };
        };
      }> = await request({
        query: GET_TEMPLATE_CATEGORIES,
        variables: { numberPerPage: 100 },
      });

      if (!this._isMounted) return;

      this.setState({
        templateCategories: data.templateCategories.results,
      });
    } catch (error) {
      message.error(`Failed to load template categories - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
  };

  redirectToNewDepartmentPage = (): void => {
    this.props.history.push("/communication-templates/new");
  };

  onFilter = (values: string[]): void => {
    this.setState(
      {
        selectedTypes: values,
        page: 1,
        isLoading: true,
        isFiltering: Boolean(
          values.length > 0 ||
            this.state.selectedCategoryIds.length > 0 ||
            this.state.searchTerm,
        ),
      },
      this.getEmailTemplates,
    );
  };

  onCategoryFilter = (values: string[]): void => {
    this.setState(
      {
        selectedCategoryIds: values,
        page: 1,
        isLoading: true,
        isFiltering: Boolean(
          values.length > 0 ||
            this.state.selectedTypes.length > 0 ||
            this.state.searchTerm,
        ),
      },
      this.getEmailTemplates,
    );
  };

  onClear = (): void => {
    this.setState(
      {
        searchTerm: "",
        selectedTypes: [],
        selectedCategoryIds: [],
        isFiltering: false,
        page: 1,
        isLoading: true,
      },
      this.getEmailTemplates,
    );
  };

  onSearch = (searchTerm: string): void => {
    this.setState(
      {
        searchTerm,
        page: 1,
        isLoading: true,
        isFiltering: Boolean(
          searchTerm ||
            this.state.selectedTypes.length > 0 ||
            this.state.selectedCategoryIds.length > 0,
        ),
      },
      this.getEmailTemplates,
    );
  };

  nextPage = async (page: number, pageSize?: number): Promise<void> => {
    this.setState(
      {
        page,
        pageSize: pageSize || this.state.pageSize,
        isLoading: true,
      },
      this.getEmailTemplates,
    );
  };

  render(): JSX.Element | null {
    const {
      templates,
      templateCategories,
      selectedCategoryIds,
      page,
      pageSize,
      templatesCount,
    } = this.state;

    return (
      <Card>
        <Fragment>
          <PageToolbar
            searchPlaceholder="Search templates by name, key, or category"
            searchValue={this.state.searchTerm}
            onSearchChange={(e) => this.onSearch(e.target.value)}
            primaryAction={
              <EntitledSection entitlement={entitlements.Templates.create}>
                <Button
                  type="primary"
                  data-testid="create-aumtarget-button"
                  onClick={this.redirectToNewDepartmentPage}
                >
                  <PlusOutlined />
                  New Template
                </Button>
              </EntitledSection>
            }
            filters={
              <PageToolbar.Filters
                isFiltering={this.state.isFiltering}
                onClear={this.onClear}
                configs={[
                  {
                    label: "Filter By Type",
                    options: [
                      { value: "sms", label: "SMS" },
                      { value: "email", label: "Email" },
                      { value: "pdf", label: "PDF" },
                    ],
                    selectedValues: this.state.selectedTypes,
                    onChange: this.onFilter,
                    width: 250,
                    icon: <MessageOutlined />,
                    mode: "single",
                  },
                  {
                    label: "Filter By Category",
                    options: templateCategories.map((templateCategory) => ({
                      value: templateCategory.id,
                      label: templateCategory.name,
                    })),
                    selectedValues: selectedCategoryIds,
                    onChange: this.onCategoryFilter,
                    width: 250,
                    icon: <AppstoreOutlined />,
                    mode: "single",
                  },
                ]}
              />
            }
            totalCount={templatesCount}
            currentDataCount={templates.length}
            page={page}
            pageSize={pageSize}
            modelName="templates"
          />
          <Row>
            <Col span={24}>
              {TemplatesTableFactory(
                templates,
                page,
                pageSize,
                templatesCount,
                this.nextPage,
              )}
            </Col>
          </Row>
        </Fragment>
      </Card>
    );
  }
}
